import React, { useMemo } from 'react';
import { useLocation } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { TripodContext, TripodContextData, TripodContextValue } from './TripodContext';

// The query key for our tripod data
const getTripodQueryKey = (path: string) => ['tripodData', path];

// The fetcher function for our query
const fetchTripodData = async (path: string): Promise<TripodContextData | null> => {
  // To safely encode the path for a URL parameter, we encode each segment
  // and join them with a safe separator (~). This avoids issues with user-
  // generated content in paths (e.g., paths containing '_').
  // A root path '/' is encoded as a single underscore for simplicity.
  const encodedPath = path === '/' ? '_' : path.split('/').filter(p => p).map(encodeURIComponent).join('~');

  const response = await fetch(`/api/tripod-data/${encodedPath}`);
  if (!response.ok) {
    throw new Error(`Failed to fetch tripod data for ${path}`);
  }
  return response.json();
};

interface TripodStateProviderProps {
  serverData?: TripodContextData;
  children: React.ReactNode;
}

export const TripodStateProvider: React.FC<TripodStateProviderProps> = ({ children }) => {
  const location = useLocation();
  const queryClient = useQueryClient();
  const path = location.pathname;

  const { data: tripodData, isLoading } = useQuery({
    queryKey: getTripodQueryKey(path),
    queryFn: () => fetchTripodData(path),
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
    refetchOnWindowFocus: false, // Avoids re-fetching on tab focus, which might be desirable
  });

  const contextValue = useMemo<TripodContextValue>(() => ({
    tripodData: tripodData ?? undefined,
    isLoading,
    // Provide a way for components to update the cache if needed
    setTripodData: (data: TripodContextData) => {
      queryClient.setQueryData(getTripodQueryKey(path), data);
    },
  }), [tripodData, isLoading, queryClient, path]);

  return <TripodContext.Provider value={contextValue}>{children}</TripodContext.Provider>;
};
